import React, { useState } from "react";
import { LocateMeButton } from "@/components/LocateMeButton";
import { MapLinkPaste } from "@/components/MapLinkPaste";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label, Switch } from "@/components/ui-kit";
import { GalleryManager } from "@/components/GalleryManager";
import { VenueMap } from "@/components/VenueMap";
import { GOVERNORATES } from "@/pages/PartnerRegister";
import { api, errorText } from "@/lib/api";
import type { Spot } from "@/lib/spots";
import { useSpotCategories } from "@/hooks/useSpotCategories";
import { usePrefs } from "@/contexts/PrefsContext";

const EMPTY = { category: "", name_ar: "", name_en: "", description_ar: "", governorate: "baghdad", area: "", address: "", hours_ar: "", gallery: [] as string[], active: true, lat: null as number | null, lng: null as number | null };
const sel = "h-11 w-full rounded-xl border bg-background px-3 text-sm";

export const SpotForm = ({ spot, onClose, onSaved, suggest = false, partner = false }: { spot: Spot | null; onClose: () => void; onSaved: () => void; suggest?: boolean; partner?: boolean }) => {
  const { t } = usePrefs();
  const [f, setF] = useState(spot ? { ...EMPTY, ...spot } : EMPTY);
  const [busy, setBusy] = useState(false);
  const cats = useSpotCategories();
  const { lang } = usePrefs();
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const txt = (k: keyof typeof EMPTY, label: string, area = false) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {area ? <textarea data-testid={`spot-form-${k}`} value={f[k] as string} onChange={(e) => set(k, e.target.value)} rows={3} className="w-full rounded-xl border bg-background p-3 text-sm" />
        : <Input data-testid={`spot-form-${k}`} value={f[k] as string} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set(k, e.target.value)} />}
    </div>
  );

  const save = async () => {
    if (f.name_ar.trim().length < 2) return toast.error(t("spot.errName"));
    if (!f.category) return toast.error(t("err.Choose a valid category"));
    if (f.lat == null || f.lng == null) return toast.error(t("spot.errLocation"));
    setBusy(true);
    const body = Object.fromEntries(Object.entries(f).filter(([k]) => k in EMPTY));
    try {
      const { category, active, ...rest } = body as Record<string, unknown>;
      await (partner ? api.post("/venue/venues", { ...rest, type: category }) : suggest ? api.post("/me/spot-suggestions", body) : spot ? api.put(`/admin/public-spots/${spot.spot_id}`, body) : api.post("/admin/public-spots", body));
      toast.success(t(partner ? "pp.suggestSent" : suggest ? "sug.sent" : "spot.saved"));
      onSaved();
    } catch (e) { toast.error(errorText(e, t)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="spot-form-dialog">
        <DialogHeader><DialogTitle>{partner ? t("pp.suggestTitle") : suggest ? t("sug.title") : spot ? t("spot.edit") : t("spot.add")}</DialogTitle></DialogHeader>
        {(suggest || partner) && <p data-testid="spot-form-hint" className="text-sm text-muted-foreground -mt-2">{t(partner ? "pp.suggestHint" : "sug.hint")}</p>}
        <div className="space-y-4">
          {txt("name_ar", t("spot.nameAr"))}
          {txt("name_en", t("spot.nameEn"))}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("spot.category")}</Label>
              <select data-testid="spot-form-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={sel}><option value="" disabled>—</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{lang === "ar" ? c.name_ar : c.name_en}</option>)}</select></div>
            <div className="space-y-1.5"><Label>{t("spot.governorate")}</Label>
              <select data-testid="spot-form-governorate" value={f.governorate} onChange={(e) => set("governorate", e.target.value)} className={sel}>{GOVERNORATES.map((g) => <option key={g} value={g}>{t(`gov.${g}`)}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">{txt("area", t("spot.area"))}{txt("address", t("spot.address"))}</div>
          {txt("hours_ar", t("spot.hours"))}
          {txt("description_ar", t("spot.description"), true)}
          <div className="space-y-1.5"><Label>{t("spot.photos")}</Label><GalleryManager value={f.gallery} onChange={(g) => set("gallery", g)} uploadPath={partner ? "/venue/uploads" : suggest ? "/me/uploads" : "/admin/uploads"} /></div>
          <div className="space-y-1.5"><Label>{t("spot.location")}</Label>
            <LocateMeButton onPick={(lat, lng) => setF((x) => ({ ...x, lat, lng }))} />
            {!suggest && <MapLinkPaste endpoint={partner ? "/venue/maps/resolve" : undefined} onPick={(lat, lng) => setF((x) => ({ ...x, lat, lng }))} />}
            <VenueMap spot lat={f.lat} lng={f.lng} onPick={(lat, lng) => setF((x) => ({ ...x, lat, lng }))} className="h-64" />
            <p className="text-xs text-muted-foreground" data-testid="spot-form-coords">{f.lat != null ? `${f.lat}, ${f.lng}` : t("spot.pickHint")}</p></div>
          {!suggest && !partner && <label className="flex items-center justify-between rounded-2xl border p-3 font-semibold">{t("spot.visible")}<Switch data-testid="spot-form-active" checked={f.active} onCheckedChange={(v: boolean) => set("active", v)} /></label>}
          <button data-testid="spot-form-save" disabled={busy} onClick={save} className="h-12 w-full rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t(suggest || partner ? "sug.send" : "spot.save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
